import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
    CreditCard,
    Receipt,
    FileText,
    Banknote,
    LayoutDashboard,
    ClipboardCheck,
    Users,
    ShieldCheck,
    Plus,
    Upload,
    RefreshCcw,
    Calendar,
    BarChart3,
    Wallet,
    Sparkles,
    ArrowRight,
    ArrowRightLeft,
    Eye,
    EyeOff,
    Moon,
    Sun,
    Coins,
    TrendingUp,
    PiggyBank,
} from "lucide-react";

const NAV_ITEMS = [
    { label: "Home", icon: LayoutDashboard, to: "/dashboard" },
    { label: "Finance Action Center", icon: ClipboardCheck, to: "/finance-review" },
    { label: "Finance Copilot", icon: Sparkles, to: "/copilot" },
    { label: "Cash Desk", icon: Wallet, to: "/cash-desk" },
    { label: "Live Forex", icon: RefreshCcw, to: "/forex" },
    { label: "Credit Cards", icon: CreditCard, to: "/cards" },
    { label: "Expenses", icon: Receipt, to: "/expenses" },
    { label: "Statements", icon: FileText, to: "/statements" },
    { label: "Payments", icon: Banknote, to: "/payments" },
    { label: "Reconciliation", icon: RefreshCcw, to: "/reconciliation" },
    { label: "Bank Accounts", icon: Wallet, to: "/bank-accounts" },
    { label: "Investments", icon: TrendingUp, to: "/investments" },
    { label: "Savings Goals", icon: PiggyBank, to: "/goals" },
    { label: "Calendar", icon: Calendar, to: "/calendar" },
    { label: "Reports", icon: BarChart3, to: "/reports" },
    { label: "Users & Roles", icon: Users, to: "/users" },
    { label: "Security Center", icon: ShieldCheck, to: "/security" },
    { label: "Legal Config Centre", icon: ShieldCheck, to: "/legal-config" },
];

/**
 * Global command palette (⌘K / Ctrl+K).
 * Doubles as an ACTION LAUNCHER — toggles hide-balance, theme, and
 * pre-opens Add dialogs on the right pages (via `?add=1` URL flag).
 */
export default function CommandPalette() {
    const [open, setOpen] = useState(false);
    const [cards, setCards] = useState([]);
    const nav = useNavigate();
    const { prefs, updatePrefs } = usePrefs();

    // ⌘K / Ctrl+K listener
    useEffect(() => {
        const down = (e) => {
            if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, []);

    // Lazy-load cards on first open
    useEffect(() => {
        if (open && cards.length === 0) {
            api.get("/cards").then((r) => setCards(r.data || [])).catch(() => {});
        }
    }, [open, cards.length]);

    // Run a callback after closing so the dialog dismount doesn't cancel state updates
    const runAction = useCallback((fn) => {
        setOpen(false);
        setTimeout(fn, 0);
    }, []);

    const go = useCallback((to) => runAction(() => nav(to)), [nav, runAction]);

    const toggleHide = useCallback(() => runAction(() => {
        const next = !prefs.hide_balance;
        updatePrefs({ hide_balance: next });
        toast(next ? "Balances hidden. Press Shift + H to reveal." : "Balances visible.");
    }), [prefs.hide_balance, updatePrefs, runAction]);

    const toggleTheme = useCallback(() => runAction(() => {
        const cur = prefs.theme || "light";
        const next = cur === "dark" ? "light" : "dark";
        updatePrefs({ theme: next });
        toast(`Switched to ${next} theme.`);
    }), [prefs.theme, updatePrefs, runAction]);

    // Fuzzy input parser — recognises "add expense 500 lunch", "cash sale 200",
    // "pay 3000 icici", etc. Returns a suggested action or null.
    const [query, setQuery] = useState("");

    // Recent merchants + categories from user's own expense history — used for
    // smart-parse autocomplete so power-users can type "lunch" and see their
    // previous merchant chip.
    const [recent, setRecent] = useState({ merchants: [], categories: [] });
    useEffect(() => {
        if (!open || recent.merchants.length > 0) return;
        api.get("/expenses?limit=200").then((r) => {
            const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
            const mcount = {}; const ccount = {};
            const merchantCatCount = {};
            for (const e of rows) {
                const m = (e.merchant || "").trim();
                const c = (e.category || "").trim();
                if (m) mcount[m] = (mcount[m] || 0) + 1;
                if (c) ccount[c] = (ccount[c] || 0) + 1;
                if (m && c) {
                    merchantCatCount[m] = merchantCatCount[m] || {};
                    merchantCatCount[m][c] = (merchantCatCount[m][c] || 0) + 1;
                }
            }
            const top = (o) => Object.entries(o).sort((a, b) => b[1] - a[1]).map(([k]) => k).slice(0, 30);
            const merchantCategory = {};
            for (const [m, cats] of Object.entries(merchantCatCount)) {
                const best = Object.entries(cats).sort((a, b) => b[1] - a[1])[0];
                if (best) merchantCategory[m] = best[0];
            }
            setRecent({ merchants: top(mcount), categories: top(ccount), merchantCategory });
        }).catch(() => {});
    }, [open, recent.merchants.length]);

    const smartHit = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        if (q.length < 6) return null;
        // capture "<verb> [type] <amount> [note...]"
        const m = q.match(
            /^(?:add|new|log|create|spent|pay|cash)\s+(expense|income|sale|transfer|payment|card)?\s*(?:of\s+)?([₹$]?\s*[\d,]+(?:\.\d{1,2})?)\s*(.*)$/,
        );
        if (!m) return null;
        const kind = (m[1] || "expense").toLowerCase();
        const rawAmt = m[2].replace(/[₹$,\s]/g, "");
        const amt = parseFloat(rawAmt);
        if (!(amt > 0)) return null;
        const note = (m[3] || "").trim();

        // Combined-intent match: infer a past merchant + its most-frequent category
        let merchantHit = null;
        let categoryHit = null;
        if (note && recent.merchants.length > 0) {
            const tokens = note.split(/\s+/);
            for (const tok of tokens) {
                const hit = recent.merchants.find((m) => m.toLowerCase().includes(tok));
                if (hit) { merchantHit = hit; break; }
            }
            if (merchantHit && recent.merchantCategory && recent.merchantCategory[merchantHit]) {
                categoryHit = recent.merchantCategory[merchantHit];
            }
        }

        const enc = (s) => encodeURIComponent(s);
        const priceLbl = prefs.hide_balance ? "" : "₹" + amt.toLocaleString("en-IN");
        const chipTag = merchantHit ? ` · ${merchantHit}${categoryHit ? " / " + categoryHit : ""}` : (note ? " · " + note : "");

        if (kind === "sale" || (q.startsWith("cash") && kind === "expense")) {
            return {
                label: `Cash sale ${priceLbl}${chipTag}`,
                to: `/cash-desk?tab=ledger&add=sale&amount=${amt}&note=${enc(merchantHit || note)}`,
                hint: "Cash desk",
            };
        }
        if (kind === "income") {
            return {
                label: `Log income ${priceLbl}${chipTag}`,
                to: `/income?add=1&amount=${amt}&source=${enc(merchantHit || note)}`,
                hint: "Income",
            };
        }
        if (kind === "payment" || q.startsWith("pay ")) {
            return {
                label: `Record card payment ${priceLbl}${chipTag}`,
                to: `/payments?add=1&amount=${amt}&remarks=${enc(merchantHit || note)}`,
                hint: "Payment tracker",
            };
        }
        // default = expense — include merchant + category prefill when we found them
        const params = new URLSearchParams({ amount: String(amt), note });
        if (merchantHit) params.set("merchant", merchantHit);
        if (categoryHit) params.set("category", categoryHit);
        return {
            label: `New expense ${priceLbl}${chipTag}`,
            to: `/quick-entry?${params.toString()}`,
            hint: merchantHit ? "Smart-fill" : "Quick entry",
        };
    }, [query, prefs.hide_balance, recent]);

    // Merchant Copilot bridge — if the query looks like a natural question,
    // offer "Ask Copilot: <query>" so users can jump from palette to LLM chat.
    const copilotHit = useMemo(() => {
        const q = (query || "").trim();
        if (q.length < 8 || smartHit) return null;
        const looksLikeQuestion = /\?$/.test(q) || /^(what|how|when|why|which|show|list|find|last)\b/i.test(q);
        if (!looksLikeQuestion) return null;
        return {
            label: `Ask Copilot: ${q.length > 60 ? q.slice(0, 60) + "…" : q}`,
            to: `/copilot?q=${encodeURIComponent(q)}`,
        };
    }, [query, smartHit]);

    // Match user's own past merchants/categories against free-text query
    const historySuggestions = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        if (q.length < 2 || smartHit) return { merchants: [], categories: [] };
        const match = (arr) => arr.filter((x) => x.toLowerCase().includes(q)).slice(0, 4);
        return { merchants: match(recent.merchants), categories: match(recent.categories) };
    }, [query, recent, smartHit]);

    // Frequency-decay ranking: each click adds 1 point; every day halves.
    // Stored as `{ key: { score: number, ts: iso } }`.
    const [recentAct, setRecentAct] = useState(() => {
        try {
            const raw = JSON.parse(localStorage.getItem("vault.cmdk.recent") || "{}");
            // Migrate from old array-schema
            if (Array.isArray(raw)) {
                const now = new Date().toISOString();
                return Object.fromEntries(raw.map((k, i) => [k, { score: 5 - i, ts: now }]));
            }
            return raw;
        } catch { return {}; }
    });
    const pushRecent = useCallback((key) => {
        setRecentAct((prev) => {
            const now = new Date();
            const decayed = {};
            for (const [k, v] of Object.entries(prev)) {
                if (!v || typeof v !== "object") continue;
                const ageDays = Math.max(0, (now - new Date(v.ts)) / 86400000);
                const halfLives = ageDays / 3;   // halve every 3 days
                const newScore = (v.score || 0) * Math.pow(0.5, halfLives);
                if (newScore >= 0.05) decayed[k] = { score: newScore, ts: v.ts };
            }
            decayed[key] = { score: (decayed[key]?.score || 0) + 1, ts: now.toISOString() };
            try { localStorage.setItem("vault.cmdk.recent", JSON.stringify(decayed)); } catch { /* noop */ }
            return decayed;
        });
    }, []);
    const recentKeys = useMemo(
        () => Object.entries(recentAct)
            .filter(([, v]) => v && typeof v === "object")
            .sort((a, b) => (b[1].score || 0) - (a[1].score || 0))
            .map(([k]) => k)
            .slice(0, 5),
        [recentAct],
    );
    const wrapRun = useCallback((key, run) => () => { pushRecent(key); run(); }, [pushRecent]);

    const ACTIONS = useMemo(() => [
        {
            key: "toggle-hide",
            label: prefs.hide_balance ? "Show balances" : "Hide balances",
            hint: "Shift + H",
            icon: prefs.hide_balance ? Eye : EyeOff,
            run: toggleHide,
        },
        {
            key: "toggle-theme",
            label: prefs.theme === "dark" ? "Switch to light theme" : "Switch to dark theme",
            hint: "Instant",
            icon: prefs.theme === "dark" ? Sun : Moon,
            run: toggleTheme,
        },
        { key: "act-copilot",   label: "Ask Finance Copilot",  hint: "AI",           icon: Sparkles,       run: () => go("/copilot") },
        { key: "act-expense",   label: "New expense",          hint: "Quick entry",  icon: Plus,           run: () => go("/quick-entry") },
        { key: "act-income",    label: "Log income",           hint: "Received",     icon: Banknote,       run: () => go("/income?add=1") },
        { key: "act-cash-sale", label: "Cash sale",            hint: "Cash desk",    icon: Coins,          run: () => go("/cash-desk?tab=ledger&add=sale") },
        { key: "act-cash-exp",  label: "Cash expense",         hint: "Cash desk",    icon: Coins,          run: () => go("/cash-desk?tab=ledger&add=expense") },
        { key: "act-cash-tx",   label: "Cash transfer",        hint: "Between accounts", icon: ArrowRightLeft, run: () => go("/cash-desk?tab=transfers&add=1") },
        { key: "act-stmt",      label: "Upload statement",     hint: "PDF",          icon: Upload,         run: () => go("/statements?add=1") },
        { key: "act-pay",       label: "Record card payment",  hint: "Payment tracker", icon: Banknote,    run: () => go("/payments?add=1") },
        { key: "act-card",      label: "Add new card",         hint: "Card master",  icon: CreditCard,     run: () => go("/cards/new") },
        { key: "act-goal",      label: "New savings goal",     hint: "Goals",        icon: PiggyBank,      run: () => go("/goals?add=1") },
        { key: "act-inv",       label: "Add investment",       hint: "Portfolio",    icon: TrendingUp,     run: () => go("/investments?add=1") },
        { key: "act-due",       label: "See cards due this week", hint: "Attention",  icon: ClipboardCheck, run: () => go("/finance-review") },
    ], [prefs.hide_balance, prefs.theme, toggleHide, toggleTheme, go]);

    const cardResults = useMemo(
        () => cards.slice(0, 40).map((c) => ({
            id: c.id,
            keywords: `${c.bank_name || ""} ${c.card_name || ""} ${c.last_4 || ""} ${c.card_holder_name || ""}`.toLowerCase(),
            label: `${c.bank_name} · ${c.card_name}`,
            sub: `•••• ${c.last_4} · ${c.card_holder_name || "—"}`,
        })),
        [cards],
    );

    return (
        <CommandDialog open={open} onOpenChange={setOpen}>
            <VisuallyHidden asChild>
                <DialogTitle>Command palette — search or run actions</DialogTitle>
            </VisuallyHidden>
            <CommandInput
                placeholder="Type to search cards, pages, actions… (⌘K)"
                value={query}
                onValueChange={setQuery}
                data-testid="cmdk-input"
            />
            <CommandList>
                <CommandEmpty>No results. Try a bank name, last 4 digits, or an action.</CommandEmpty>
                {!query && recentKeys.length > 0 && (
                    <CommandGroup heading="Recent · most used">
                        {recentKeys.map((key) => {
                            const act = ACTIONS.find((a) => a.key === key);
                            if (!act) return null;
                            const Ic = act.icon;
                            return (
                                <CommandItem
                                    key={`recent-${key}`}
                                    onSelect={wrapRun(act.key, act.run)}
                                    data-testid={`cmdk-recent-${key}`}
                                    value={`recent ${act.label}`}
                                >
                                    <Ic className="mr-2 h-4 w-4 text-amber-500" />
                                    <div className="flex-1 min-w-0">
                                        <div>{act.label}</div>
                                        {recentAct[key]?.score >= 2 && (
                                            <div className="text-[10.5px] text-stone-500">
                                                used {Math.round(recentAct[key].score)}× recently
                                            </div>
                                        )}
                                    </div>
                                    <span className="ml-auto text-[11px] text-stone-500">Recent</span>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                )}
                {smartHit && (
                    <CommandGroup heading="Smart action">
                        <CommandItem
                            key="smart-hit"
                            onSelect={() => runAction(() => nav(smartHit.to))}
                            data-testid="cmdk-smart-hit"
                            value={`smart ${query}`}
                        >
                            <Sparkles className="mr-2 h-4 w-4 text-amber-500" />
                            <span>{smartHit.label}</span>
                            <span className="ml-auto text-[11px] text-stone-500">{smartHit.hint}</span>
                        </CommandItem>
                    </CommandGroup>
                )}
                {copilotHit && (
                    <CommandGroup heading="Ask Copilot">
                        <CommandItem
                            key="copilot-hit"
                            onSelect={() => runAction(() => nav(copilotHit.to))}
                            data-testid="cmdk-copilot-hit"
                            value={`copilot ${query}`}
                        >
                            <Sparkles className="mr-2 h-4 w-4 text-purple-500" />
                            <span>{copilotHit.label}</span>
                            <span className="ml-auto text-[11px] text-stone-500">AI · tenant-scoped</span>
                        </CommandItem>
                    </CommandGroup>
                )}
                {(historySuggestions.merchants.length > 0 || historySuggestions.categories.length > 0) && (
                    <CommandGroup heading="From your history">
                        {historySuggestions.merchants.map((m) => (
                            <CommandItem
                                key={`hist-m-${m}`}
                                onSelect={() => runAction(() => nav(`/expenses?q=${encodeURIComponent(m)}`))}
                                data-testid={`cmdk-hist-merchant-${m.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                                value={`merchant ${m}`}
                            >
                                <Receipt className="mr-2 h-4 w-4" />
                                <span>{m}</span>
                                <span className="ml-auto text-[11px] text-stone-500">Past merchant</span>
                            </CommandItem>
                        ))}
                        {historySuggestions.categories.map((c) => (
                            <CommandItem
                                key={`hist-c-${c}`}
                                onSelect={() => runAction(() => nav(`/quick-entry?category=${encodeURIComponent(c)}`))}
                                data-testid={`cmdk-hist-cat-${c.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                                value={`category ${c}`}
                            >
                                <Sparkles className="mr-2 h-4 w-4" />
                                <span>{c}</span>
                                <span className="ml-auto text-[11px] text-stone-500">Past category</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}
                <CommandGroup heading="Actions">
                    {ACTIONS.map((a) => (
                        <CommandItem
                            key={a.key}
                            onSelect={wrapRun(a.key, a.run)}
                            data-testid={`cmdk-${a.key}`}
                        >
                            <a.icon className="mr-2 h-4 w-4" />
                            <span>{a.label}</span>
                            <span className="ml-auto text-[11px] text-stone-500">{a.hint}</span>
                        </CommandItem>
                    ))}
                </CommandGroup>
                <CommandSeparator />
                <CommandGroup heading="Navigate">
                    {NAV_ITEMS.map((n) => (
                        <CommandItem
                            key={n.to}
                            onSelect={() => go(n.to)}
                            data-testid={`cmdk-nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            <n.icon className="mr-2 h-4 w-4" />
                            <span>{n.label}</span>
                            <ArrowRight className="ml-auto h-3 w-3 text-stone-400" />
                        </CommandItem>
                    ))}
                </CommandGroup>
                {cardResults.length > 0 && (
                    <>
                        <CommandSeparator />
                        <CommandGroup heading="Cards">
                            {cardResults.map((c) => (
                                <CommandItem
                                    key={c.id}
                                    value={c.keywords}
                                    onSelect={() => go(`/cards/${c.id}/view`)}
                                    data-testid={`cmdk-card-${c.id}`}
                                >
                                    <CreditCard className="mr-2 h-4 w-4" />
                                    <div className="flex-1 min-w-0">
                                        <div className="truncate">{c.label}</div>
                                        <div className="text-[11px] text-stone-500 truncate">{c.sub}</div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </>
                )}
            </CommandList>
        </CommandDialog>
    );
}
